import {ReactElement} from "react";
import {VersionType} from "../../../../../__generated__/generated_types";
import {formatterDollarUSMax2Decimal} from '../../../../../utils/fotmatters';
import {RenderAuditLogsEnrichment, RendererProps} from "../RendererProps";
import * as common from "../styles/common.module.scss";


interface GroupedWorksheetLineData {
    id: string,
    name: string | null;
    seasonLabel: string | null;
    accountName: string | null;
    propertyName: string | null;
    versionType: VersionType | null;
    updates: {monthIndex: number, value: number}[],
}

export interface RenderUpdateWorksheetLineAssumptionsProps extends RendererProps {
    enrichment: RenderAuditLogsEnrichment | undefined;
}

export function RenderUpdateWorksheetLineAssumptions(props: RenderUpdateWorksheetLineAssumptionsProps): ReactElement {
    const groupedLines: GroupedWorksheetLineData[] = [];
    const accounts: Set<string> = new Set();
    const properties: Set<string> = new Set();

    const updateData = props.variables["updates"] ?? [];
    const budgetYear = props.variables["budgetYear"] ?? null;

    if (Array.isArray(updateData)) {
        for (const update of updateData) {
            const lineId = update["lineId"] ?? null;

            if (lineId === null) {
                continue;
            }

            // We progressively enhance the data to be displayed, based on what we
            // were able to pull from the audit log for each entry. This way, it works
            // whether we only have a list of month/value pairs, up to the full set of enriched data.
            const builtLineData: GroupedWorksheetLineData = {
                id: lineId,
                name: null,
                seasonLabel: null,
                accountName: null,
                propertyName: null,
                versionType: null,
                updates: [],
            }

            const lineValuesData = update["lineValues"];
            // The array of month/value changes is the bare minimum needed to display a meaningful assumptions update log entry
            if (!Array.isArray(lineValuesData)) {
                continue;
            }

            for (const lineValue of lineValuesData) {
                const monthIndex = lineValue["monthIndex"] ?? null;
                const value = Number(lineValue["value"] ?? 0); // null is a valid db value, but 0 goes into calculations the same so I'm just falling back to 0 for display
                if (monthIndex !== null) {
                    builtLineData.updates.push({monthIndex, value});
                }
            }

            const enrichedData = props.enrichment?.worksheetLinesExtraInfo.find(x => x.id === lineId);
            if (enrichedData) {
                builtLineData.name = enrichedData.name;
                builtLineData.accountName = enrichedData.accountName;
                builtLineData.propertyName = enrichedData.propertyName;
                builtLineData.versionType = enrichedData.versionType;

                accounts.add(builtLineData.accountName);
                properties.add(builtLineData.propertyName);

                // I dont think the UI has a way to update across multiple versionTypes? We just pass in budgetYear, though,
                // so I think technically it could, so to be on the safe side I'm building out the year/period text per group.
                if (budgetYear !== null && builtLineData.versionType !== null) {
                    const displayYear = builtLineData.versionType === VersionType.Reforecast ? budgetYear - 1 : budgetYear;
                    const season = builtLineData.versionType === VersionType.Reforecast ? "Reforecast" : "Budget";
                    builtLineData.seasonLabel = `${displayYear} (${season})`;
                }
            }

            groupedLines.push(builtLineData);
        }
    }

    return (
        <div>
            {groupedLines.map(lineData => (
                <div className={common.section}>
                    {lineData.seasonLabel !== null &&
                        <h4 className={common.textHeader}>{lineData.seasonLabel}</h4>
                    }

                    <h4 className={common.textHeader}>Property: {lineData.propertyName} | Account: {lineData.accountName}</h4>

                    <h4 className={common.textHeader}>{lineData.name ?? "Worksheet Line"}</h4>
                    {lineData.updates.map(({monthIndex, value}) =>
                        <div key={monthIndex}>Month: {monthIndex + 1} Value: {formatterDollarUSMax2Decimal.format(Number(value))}</div>
                    )}
                </div>
            ))}
        </div>
    );
}
